import React from 'react';
import { motion } from 'framer-motion';
import {
  ShieldCheck, RefreshCw, HelpCircle, Repeat, Crown,
  Package, LogOut, Building2, Infinity as InfinityIcon
} from 'lucide-react';
import { Button } from '../ui/button';

const DashboardHeader = ({
  user,
  subscription,
  isAdminUser,
  isProductOwner,
  switchingPack,
  refreshing,
  onSwitchEdition,
  onRefresh,
  onTour,
  onLogout,
}) => {
  const displayName = user?.name || user?.email?.split('@')[0] || 'there';

  return (
    <>
      <nav className="sticky top-0 z-40 border-b border-white/10 bg-slate-950/75 backdrop-blur-2xl" data-testid="dashboard-nav-header">
        <div className="mx-auto flex min-h-16 w-full max-w-screen-2xl items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-3" data-testid="dashboard-nav-brand">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-emerald-400/30 bg-emerald-400/10">
              <ShieldCheck className="h-5 w-5 text-emerald-300" />
            </div>
            <div className="min-w-0">
              <p className="truncate font-heading text-base font-semibold text-white sm:text-lg">AIVORA Comply360</p>
              <p className="hidden text-[11px] text-slate-500 sm:block">ISO 27001:2022 Command Center</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 lg:flex" data-testid="dashboard-user-identity">
              <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_12px_rgba(16,185,129,0.8)]" />
              <span className="max-w-52 truncate text-xs text-slate-300">{user?.email}</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onRefresh}
              disabled={refreshing}
              className="border border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white"
              data-testid="dashboard-refresh-button"
              title="Refresh dashboard"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onTour}
              className="hidden border border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white sm:inline-flex"
              data-testid="dashboard-tour-button"
              title="Replay dashboard tour"
            >
              <HelpCircle className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onLogout}
              className="border border-white/10 bg-white/5 text-slate-300 hover:bg-red-400/10 hover:text-red-300"
              data-testid="dashboard-logout-button"
              title="Logout"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </nav>

      <div className="mx-auto w-full max-w-screen-2xl px-4 pb-8 pt-10 sm:px-6 lg:px-8">
        <motion.header
          className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          data-tour="dashboard-header"
          data-testid="dashboard-welcome-header"
        >
        <div className="min-w-0">
          <p className="dashboard-eyebrow mb-3">Compliance workspace</p>
          <h1 className="font-heading text-4xl font-semibold text-white sm:text-5xl" data-testid="dashboard-welcome-title">
            Welcome back, {displayName}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-400 sm:text-base" data-testid="dashboard-welcome-description">
            {isAdminUser
              ? 'Monitor assessment readiness, control health, and organizational progress from one view.'
              : 'Track your ISO 27001 maturity, close control gaps, and keep your assessment moving.'}
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            {subscription?.plan && (
              <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${subscription.plan === 'pro' ? 'border-amber-400/30 bg-amber-400/10 text-amber-200' : 'border-slate-400/20 bg-slate-400/10 text-slate-200'}`} data-testid="dashboard-edition-badge">
                {subscription.plan === 'pro' ? <Crown className="h-3.5 w-3.5" /> : <Package className="h-3.5 w-3.5" />}
                {subscription.plan === 'pro' ? 'Pro Edition' : 'Standard Edition'}
              </span>
            )}
            {isAdminUser && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1.5 text-xs font-semibold text-emerald-200" data-testid="dashboard-admin-badge">
                <ShieldCheck className="h-3.5 w-3.5" /> Administrator
              </span>
            )}
            {isProductOwner && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-400/30 bg-sky-400/10 px-3 py-1.5 text-xs font-semibold text-sky-200" data-testid="dashboard-product-owner-badge">
                <InfinityIcon className="h-3.5 w-3.5" /> Product Owner
              </span>
            )}
            {user?.org_name && (
              <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-slate-300" data-testid="dashboard-organization-badge">
                <Building2 className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{user.org_name}</span>
              </span>
            )}
          </div>
        </div>

        {isProductOwner && subscription?.plan && (
          <Button
            variant="outline"
            onClick={onSwitchEdition}
            disabled={switchingPack}
            className="w-full border-emerald-400/30 bg-emerald-400/10 text-emerald-100 hover:bg-emerald-400/20 hover:text-white sm:w-auto"
            data-testid="dashboard-edition-switch-button"
          >
            <Repeat className={`mr-2 h-4 w-4 ${switchingPack ? 'animate-spin' : ''}`} />
            {switchingPack ? 'Switching edition' : `View ${subscription.plan === 'pro' ? 'Standard' : 'Pro'} Edition`}
          </Button>
        )}
        </motion.header>
      </div>
    </>
  );
};

export default DashboardHeader;